import { ColorSpectrum } from "./ColorSpectrum";

interface ToolbarProps {
  color: string;
  width: number;
  onColorChange: (color: string) => void;
  onWidthChange: (width: number) => void;
  disabled?: boolean;
  isEraser?: boolean;
  onEraserToggle?: () => void;
  onEraserOff?: () => void;
  isAnimationMode?: boolean;
  onToggleAnimationMode?: () => void;
}

const ChevronUp = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const EraserIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 20H7L3 16a1 1 0 0 1 0-1.41l10-10a1 1 0 0 1 1.41 0l7 7a1 1 0 0 1 0 1.41L17 17" />
    <path d="m6.5 15.5 5-5" />
  </svg>
);

const TimelineIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
    <line x1="7" y1="2" x2="7" y2="22" />
    <line x1="17" y1="2" x2="17" y2="22" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="2" y1="7" x2="7" y2="7" />
    <line x1="2" y1="17" x2="7" y2="17" />
    <line x1="17" y1="7" x2="22" y2="7" />
    <line x1="17" y1="17" x2="22" y2="17" />
  </svg>
);

export function Toolbar({
  color,
  width,
  onColorChange,
  onWidthChange,
  disabled = false,
  isEraser = false,
  onEraserToggle,
  onEraserOff,
  isAnimationMode = false,
  onToggleAnimationMode,
}: ToolbarProps) {
  const handleColorClick = (c: string) => {
    onColorChange(c);
    if (isEraser && onEraserOff) onEraserOff();
  };

  return (
    <div className="sidebar-toolbar">
      <ColorSpectrum onColorSelect={handleColorClick} />

      <div className="sidebar-toolbar__swatches">
        <button
          className={`sidebar-toolbar__swatch sidebar-toolbar__swatch--white ${color === "#ffffff" && !isEraser ? "selected" : ""}`}
          onClick={() => handleColorClick("#ffffff")}
          disabled={disabled}
          aria-label="White"
        />
        <button
          className={`sidebar-toolbar__swatch sidebar-toolbar__swatch--black ${color === "#000000" && !isEraser ? "selected" : ""}`}
          onClick={() => handleColorClick("#000000")}
          disabled={disabled}
          aria-label="Black"
        />
      </div>

      <div className="sidebar-toolbar__stepper">
        <button
          className="sidebar-toolbar__stepper-btn"
          onClick={() => onWidthChange(Math.min(20, width + 1))}
          disabled={disabled || width >= 20}
        >
          <ChevronUp />
        </button>
        <span className="sidebar-toolbar__stepper-value">{width}px</span>
        <button
          className="sidebar-toolbar__stepper-btn"
          onClick={() => onWidthChange(Math.max(2, width - 1))}
          disabled={disabled || width <= 2}
        >
          <ChevronDown />
        </button>
      </div>

      {onEraserToggle && (
        <button
          className={`sidebar-toolbar__btn sidebar-toolbar__btn--eraser ${isEraser ? "active" : ""}`}
          onClick={onEraserToggle}
          disabled={disabled}
          title={isEraser ? "Switch to pen" : "Eraser"}
        >
          <EraserIcon />
        </button>
      )}

      {onToggleAnimationMode && (
        <button
          className={`sidebar-toolbar__btn sidebar-toolbar__btn--timeline ${isAnimationMode ? "active" : ""}`}
          onClick={onToggleAnimationMode}
          disabled={disabled}
          title={isAnimationMode ? "Disable timeline" : "Enable timeline"}
        >
          <TimelineIcon />
        </button>
      )}
    </div>
  );
}
